// src/lib/colorScale.ts
export type HSL = { h: number; s: number; l: number }

const clamp01 = (x: number) => Math.max(0, Math.min(1, x))
const lerp = (a: number, b: number, t: number) => a + (b - a) * t

function lerpHue(a: number, b: number, t: number) {
  const aN = ((a % 360) + 360) % 360
  const bN = ((b % 360) + 360) % 360
  let d = bN - aN
  if (d > 180) d -= 360
  if (d < -180) d += 360
  return (aN + d * t + 360) % 360
}

export function toHslCss(c: HSL) {
  const h = ((c.h % 360) + 360) % 360
  const s = Math.max(0, Math.min(100, c.s))
  const l = Math.max(0, Math.min(100, c.l))
  return `hsl(${h}, ${s}%, ${l}%)`
}
export type TriScaleMode = 'blend' | 'locked' | 'banded'

export type TriScaleOptions = {
  min?: number        // default 0
  mid?: number        // default 50
  max?: number        // default 100
  low: HSL
  midColor: HSL
  high: HSL
  neutral: HSL | string
  mode?: TriScaleMode // default 'blend'
  band?: {
    lowMax: number // default 33
    midMax: number // default 66
  }
  // legacy compatibility (optional)
  legacyNeutralValue?: number // default 999
}


export function createTriScale(opts: TriScaleOptions) {
  const min = opts.min ?? 0
  const mid = opts.mid ?? 50
  const max = opts.max ?? 100

  const low = opts.low
  const midColor = opts.midColor
  const high = opts.high

  const neutralCss = typeof opts.neutral === 'string' ? opts.neutral : toHslCss(opts.neutral)
  const legacyNeutralValue = opts.legacyNeutralValue ?? 999
  // precompute split
  const tMid = (mid - min) / (max - min)


  const bandLowMax = opts.band?.lowMax ?? 33
  const bandMidMax = opts.band?.midMax ?? 66

  function colorForLow()  { return toHslCss(low) }
  function colorForMid()  { return toHslCss(midColor) }
  function colorForHigh() { return toHslCss(high) }
  const mode = opts.mode ?? 'blend'
  function at(value: number | null | undefined): string {
    if (value == null) return neutralCss
    if (value === legacyNeutralValue) return neutralCss

    if (mode === 'banded') {
      if (value <= bandLowMax) return colorForLow()
      if (value <= bandMidMax) return colorForMid()
      return colorForHigh()
    }

    // clamp to [min..max]
    const t = clamp01((value - min) / (max - min))

    if (t <= tMid) {
      const u = tMid <= 0 ? 0 : t / tMid

      // if (mode === 'locked') {
      //   return toHslCss({
      //     h: low.h,
      //     s: lerp(low.s, midColor.s, u),
      //     l: lerp(low.l, midColor.l, u),
      //   })
      // }

      // blend (existing)
      return toHslCss({
        h: lerpHue(low.h, midColor.h, u),
        s: lerp(low.s, midColor.s, u),
        l: lerp(low.l, midColor.l, u),
      })
    } else {
      const u = tMid >= 1 ? 0 : (t - tMid) / (1 - tMid)

      // if (mode === 'locked') {
      //   return toHslCss({
      //     h: high.h,
      //     s: lerp(midColor.s, high.s, u),
      //     l: lerp(midColor.l, high.l, u),
      //   })
      // }

      // blend (existing)
      return toHslCss({
        h: lerpHue(midColor.h, high.h, u),
        s: lerp(midColor.s, high.s, u),
        l: lerp(midColor.l, high.l, u),
      })
    }
  }

  return {
    at,
    neutralCss,
    legacyNeutralValue,
  }
}

export type TriScale = ReturnType<typeof createTriScale>
